(() => {
  const moneySpanPattern = /<\/?span\b[^>]*class\s*=\s*["']?money["']?[^>]*>/gi;
  const closingSpanPattern = /<\/span>/gi;
  const freeProductPattern = /You(?:'|\u2019)re\s+(.+?)\s+away from a free product!/i;
  const locale = (document.documentElement.lang || 'fr').toLowerCase().split('-')[0];
  const freeProductProgress = {
    fr: 'Plus que $1 pour obtenir un article gratuit !',
    en: 'Only $1 more to get a free product!',
    de: 'Nur noch $1 bis zu deinem Gratisartikel!',
    es: '¡Solo faltan $1 para conseguir un producto gratis!',
    it: 'Mancano solo $1 per ricevere un prodotto gratis!',
    nl: 'Nog $1 voor een gratis product!',
    pt: 'Faltam apenas $1 para receber um produto grátis!',
    ja: '無料商品まであと$1です！'
  }[locale] || 'Only $1 more to get a free product!';
  const cleanupRootSelector = 'cart-drawer, .ocu-cart-drawer, .ocu-popup.ocu-pre';
  const ocuRootSelector = '.ocu-cart-drawer, .ocu-popup.ocu-pre';
  const discoveryTriggerSelector = [
    cleanupRootSelector,
    '#cart-icon',
    '[name="add"]',
    'form[action*="/cart/add"]',
    '[name="checkout"]',
    'a[href*="/checkout"]'
  ].join(', ');
  const observedRoots = new WeakSet();
  let discoveryObserver;
  let discoveryTimer;

  const stripMoneyMarkup = (value) => {
    if (typeof value !== 'string') return value;
    return value
      .replace(moneySpanPattern, '')
      .replace(closingSpanPattern, '');
  };

  const sanitizeMoneyFormats = () => {
    if (window.Shopify) {
      window.Shopify.money_format = stripMoneyMarkup(window.Shopify.money_format);
      window.Shopify.money_with_currency_format = stripMoneyMarkup(window.Shopify.money_with_currency_format);
    }

    if (window.theme?.settings) {
      window.theme.settings.moneyFormat = stripMoneyMarkup(window.theme.settings.moneyFormat);
      window.theme.settings.moneyWithCurrencyFormat = stripMoneyMarkup(window.theme.settings.moneyWithCurrencyFormat);
    }
  };

  const cleanTextNode = (node) => {
    if (
      node.nodeType !== Node.TEXT_NODE
      || !node.nodeValue
      || node.parentElement?.closest('script, style, template')
    ) {
      return;
    }

    const current = node.nodeValue;
    const cleaned = current
      .replace(moneySpanPattern, '')
      .replace(closingSpanPattern, '')
      .replace(freeProductPattern, freeProductProgress);

    if (cleaned !== current) {
      node.nodeValue = cleaned;
    }
  };

  const cleanRoot = (root) => {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      cleanTextNode(root);
      return;
    }

    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

    const text = root.textContent || '';
    if (text.includes('<span') || text.includes('free product')) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        cleanTextNode(node);
        node = walker.nextNode();
      }
    }

    if (root.nodeType === Node.ELEMENT_NODE) {
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) {
          observeRoot(el.shadowRoot);
          cleanRoot(el.shadowRoot);
        }
      });
    }
  };

  const observeRoot = (root) => {
    if (!root || observedRoots.has(root) || !('MutationObserver' in window)) return false;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          cleanTextNode(mutation.target);
        }

        mutation.addedNodes.forEach((node) => {
          cleanRoot(node);

          if (node.nodeType === Node.ELEMENT_NODE && node.shadowRoot) {
            observeRoot(node.shadowRoot);
            cleanRoot(node.shadowRoot);
          }
        });
      });
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });

    observedRoots.add(root);
    return true;
  };

  const registerRoot = (root) => {
    if (!(root instanceof Element) || root.parentElement?.closest(cleanupRootSelector)) return false;

    const registered = observeRoot(root);
    if (registered) cleanRoot(root);
    return registered;
  };

  const registerRoots = (scope) => {
    if (!scope) return false;

    let registeredOcuRoot = false;
    const register = (root) => {
      if (registerRoot(root) && root.matches(ocuRootSelector)) {
        registeredOcuRoot = true;
        document.dispatchEvent(new CustomEvent('dg:ocu-root-ready', { detail: root }));
      }
    };

    if (scope instanceof Element && scope.matches(cleanupRootSelector)) register(scope);
    scope.querySelectorAll?.(cleanupRootSelector).forEach(register);
    return registeredOcuRoot;
  };

  const stopDiscovery = () => {
    discoveryObserver?.disconnect();
    window.clearTimeout(discoveryTimer);
    discoveryTimer = undefined;
  };

  const startDiscovery = (duration = 8000) => {
    if (!document.body || !('MutationObserver' in window)) return;
    registerRoots(document);

    if (!discoveryObserver) {
      discoveryObserver = new MutationObserver((mutations) => {
        let foundOcuRoot = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (registerRoots(node)) foundOcuRoot = true;
          });
        });

        if (foundOcuRoot) {
          window.clearTimeout(discoveryTimer);
          discoveryTimer = window.setTimeout(stopDiscovery, 1000);
        }
      });
    }

    discoveryObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    window.clearTimeout(discoveryTimer);
    discoveryTimer = window.setTimeout(stopDiscovery, duration);
  };

  const handlePossibleCartAppOpen = (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target?.closest(discoveryTriggerSelector)) return;
    startDiscovery(5000);
  };

  window.dgRequestOcuDiscovery = startDiscovery;

  const init = () => {
    if (!document.body) return;

    sanitizeMoneyFormats();
    registerRoots(document);
    startDiscovery();

    document.addEventListener('click', handlePossibleCartAppOpen, true);
    document.addEventListener('submit', handlePossibleCartAppOpen, true);
    document.addEventListener('dispatch:cart-drawer:open', () => startDiscovery(5000));

    // Perf/INP : une seule passe de sécurité différée, exécutée quand le thread est libre,
    // au lieu de 6 passes en cascade (100→7000ms). Elle reste limitée aux interfaces
    // panier/OCU ; l'observer de découverte du body se déconnecte ensuite automatiquement.
    const safetyPass = () => {
      sanitizeMoneyFormats();
      registerRoots(document);
      document.querySelectorAll(cleanupRootSelector).forEach(cleanRoot);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(safetyPass, { timeout: 3000 });
    } else {
      window.setTimeout(safetyPass, 1500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
